import AppBar from "@material-ui/core/AppBar";
import Checkbox from "@material-ui/core/Checkbox";
import CloseIcon from "@material-ui/icons/Close";
import Dialog from "@material-ui/core/Dialog";
import IconButton from "@material-ui/core/IconButton";
import Slide from "@material-ui/core/Slide";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
/* eslint-disable react/display-name */
import React, { FC, useCallback, useEffect, useState } from "react";
import { AxiosError } from "axios";
import { GetApp } from "@material-ui/icons";
import { GridColDef } from "@material-ui/data-grid";
import { TransitionProps } from "@material-ui/core/transitions";
import { makeStyles } from "@material-ui/core/styles";

import BaseDataGrid from "../../../components/modules/BaseDataGrid";
import Button from "../../../components/elements/Button";
import Loader from "../../../components/elements/Loader";
import Switch from "../../../components/elements/Switch";
import TimeFrameSelector from "../CampaignsDashboard/components/CampaignReportSectionComponents/TimeFrameSelector";
import api from "../../../api";
import csvDownload from "../../../utils/csvExport";
import { AdLibCampaignWithReports } from "../CampaignsDashboard/components/LineItemsReportSectionComponents/CampaignLineItemsReportDashboard";
import { BrandgenCampaignPool, ReportTimeFrames } from "../../../types/campaignTypes";
import {
  currencySortComparator,
  flatSortComparator,
  percentageSortComparator,
  transformRowValue,
} from "../../../helpers/campainRowFormatHelpers";
import { infoToaster, successToaster } from "../../../helpers/toasters";

const useStyles = makeStyles((theme) => ({
  appBar: {
    position: "relative",
  },
  title: {
    marginLeft: theme.spacing(2),
    flex: 1,
  },
  container: {
    height: "100vh",
  },
  headerCheckbox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
  },
  button: {
    fontSize: "0.875rem",
    background: "none",
    lineHeight: "1.75",
    textTransform: "uppercase",
    fontWeight: 500,
    minWidth: "64px",
    marginRight: "10px",
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.04);",
    },
  },
  timeFrameSelector: {
    marginRight: "10px",

    "& .MuiFormLabel-root": {
      color: "#FFF",
    },
    "& .MuiInputBase-input": {
      color: "#FFF",
    },
    "& svg": {
      color: "#FFF",
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "#FFF",
      },
      "&:hover fieldset": {
        borderColor: "#FFF",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#FFF",
      },
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "#FFF",
    },
    "& .MuiFormHelperText-root": {
      color: "#FFF",
    },
    "& .MuiInput-underline:before": {
      borderBottom: "1px solid #FFF",
    },
  },
}));

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children?: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

type SetStateAction<S> = S | ((prevState: S) => S);

type CampaignsDialogProps = {
  isOpen: boolean;
  campaignPools: BrandgenCampaignPool[];
  advertiserName: string;
  handleClose: () => void;
  setWereCampaignsEdited: (wereCampaignsEdited: SetStateAction<boolean>) => void;
};

enum EditableFields {
  Active = "active",
  Archived = "archived",
}

type CampaignRow = {
  id: string;
  name: string;
  start_date: string;
  end_date: string;
  active: boolean;
  archived: boolean;
  matchedAccounts: number;
  clicks: number | string;
  clickThroughRate: number | string;
};

const updateCampagin = async (
  brandgenCampaignPoolId: string,
  body: Partial<{ archived: boolean; active: boolean }>,
) => {
  try {
    const { active, archived } = body;
    if (archived !== undefined) await api.archiveCampaign(brandgenCampaignPoolId, { archived });
    if (active !== undefined) await api.updateCampaignStatus(brandgenCampaignPoolId, { active });
  } catch (error) {
    console.log("editCampagin error: ", error);
  }
};

const fetchLineItemsWithReport = async (brandgenCampaignPoolId: string, selectedTimeFrame: string) => {
  try {
    const { payload } = await api.getLineItemReports(brandgenCampaignPoolId, selectedTimeFrame);
    return payload;
  } catch (error) {
    const err = error as AxiosError;
    throw new Error(err.message);
  }
};

const fetchLineItemsDailyReport = async (brandgenCampaignPoolId: string, date: Date) => {
  try {
    const { payload } = await api.getLineItemDailyReport(brandgenCampaignPoolId, date);
    return payload;
  } catch (error) {
    const err = error as AxiosError;
    throw new Error(err.message);
  }
};

const fetchCampaignDailyReport = async (bgCampaignd: string, date: Date) => {
  try {
    const { payload } = await api.getCampaignDailyReport(bgCampaignd, date);
    return payload;
  } catch (error) {
    const err = error as any;
    throw new Error(err.message);
  }
};

const CampaignsDialog: FC<CampaignsDialogProps> = ({
  isOpen,
  campaignPools,
  advertiserName,
  handleClose,
  setWereCampaignsEdited,
}) => {
  const muiClasses = useStyles();
  const [rows, setRows] = useState<CampaignRow[]>([]);
  const [campaignCustomReports, setCampaignCustomReports] = useState<CampaignRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExportLoading, setIsExportLoading] = useState(false);
  const [editedRows, setEditedRows] = useState<CampaignRow[]>([]);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [adLibCampaignReports, setAdLibCampaignReports] = useState<AdLibCampaignWithReports[]>([]);

  const [selectedTimeFrame, setSelectedTimeFrame] = React.useState<ReportTimeFrames>(ReportTimeFrames.ALL_TIME);

  const [isCustomDateChanged, setIsCustomDateChanged] = useState(false);
  const [customReportDate, setCustomReportDate] = React.useState<Date>();

  const handleTimeFrameChange = useCallback(
    (
      event: React.ChangeEvent<{
        value: unknown;
        custom_date?: Date;
      }>,
    ) => {
      const { value } = event.target;
      const date = event.target?.custom_date;
      if (
        value === ReportTimeFrames.CUSTOM_DATE &&
        date &&
        new Date(date).getTime() !== new Date(customReportDate ?? "").getTime()
      ) {
        setCustomReportDate(date);
        setIsCustomDateChanged(true);
      } else {
        setIsCustomDateChanged(false);
      }
      setSelectedTimeFrame(value as ReportTimeFrames);
    },
    [customReportDate, setIsCustomDateChanged],
  );

  const excludedKeys = new Set([
    "beeswaxAdvertiserId",
    "beeswaxCampaignId",
    "beeswaxLineItemId",
    "brandgenLineItemReportId",
    "createdAt",
    "updatedAt",
    "nonBilledDataCost",
    "id",
    "adLibCampaignId",
    "brandgenCampaignId",
    "adLibAdvertiserId",
    "campaignName",
    "currencyCode",
    "date",
    "billedDataCost",
    "media_spend",
    "net_margin",
    "net_margin_percent",
    "revenue",
    "gross_margin",
    "gross_margin_percent",
    "active",
    "video_q1",
    "video_q3",
    "video_skips",
    "average_viewable_seconds",
    "invalid_impression_percent",
    "invalid_impressions",
    "measurable_impression_percent",
    "measurable_impressions",
    "measurable_invalid_impressions",
    "vcpm",
    "viewability_percent",
    "viewability_measurability_percent",
    "viewable_impressions",
    "viewable_seconds",
    "conversion_orders",
    "conversions_value",
    "postback_conversions",
    "reach",
    "video_midpoints",
    "click_through_conversions",
    "spend_per_conversion",
    "viewthrough_conversions",
    "inventory_cost",
    "video_companion_clicks",
    "video_companion_ctr",
    "video_companion_views",
  ]);

  const campaignHeaders = [
    "Campaign Name",
    "Campaign Active",
    "Campaign Description",
    "Start Date",
    "End Date",
    "Campaign Daily Budget",
    "Campaign Budget",
    "Campaign Accounts",
    "Campaign Archived",
    "Time Frame",
  ];

  const lineItemsHeaders = ["Line Item Budget" /* , "Line Item Spend" */, "Line Item Active"];

  const handleCampaignFieldChange = (editedRow: CampaignRow, field: EditableFields) => () => {
    setRows(rows.map((row) => (row.id === editedRow.id ? { ...row, [field]: !row[field] } : row)));

    const editedRowToSave = { ...editedRow, [field]: !editedRow[field] };
    const originalRow = campaignPools.find((campaignPool) => campaignPool.brandgenCampaignPoolId === editedRow.id)!;

    const fieldsToCheckFor = Object.values(EditableFields);

    let areRowsDifferent = false;

    for (const editableField of fieldsToCheckFor) {
      if (editedRowToSave[editableField] !== originalRow[editableField]) {
        areRowsDifferent = true;
        break;
      }
    }

    if (areRowsDifferent) {
      const foundEditedRow = editedRows.find((row) => row.id === editedRow.id);

      if (foundEditedRow) {
        setEditedRows(editedRows.map((row) => (row.id === editedRow.id ? editedRowToSave : row)));
      } else {
        setEditedRows([...editedRows, editedRowToSave]);
      }
    } else {
      setEditedRows(editedRows.filter((row) => row.id !== editedRow.id));
    }
  };

  const handleSave = async () => {
    if (editedRows.length === 0) {
      infoToaster("There are no changes in rows to save.");
    } else {
      setIsLoading(true);
      await Promise.all(
        editedRows.map((changedRow) =>
          updateCampagin(changedRow.id, {
            active: changedRow.active,
            archived: changedRow.archived,
          }),
        ),
      );
      setIsLoading(false);
      setWereCampaignsEdited(true);
      successToaster("Campaigns are successfully updated!", 4000);
      handleClose();
    }
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedRows(rows.map((row) => row.id));
      setIsAllSelected(true);
    } else {
      setSelectedRows([]);
      setIsAllSelected(false);
    }
  };

  const handleCheckboxChange = (brandgenCampaignPoolId: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedRows([...selectedRows, brandgenCampaignPoolId]);
    } else {
      setSelectedRows(selectedRows.filter((rowId) => rowId !== brandgenCampaignPoolId));
    }
  };

  const columns: GridColDef[] = [
    {
      field: "checkbox",
      headerName: "Export All",
      disableColumnMenu: true,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderHeader: () => (
        <div className={muiClasses.headerCheckbox}>
          <Checkbox
            checked={isAllSelected}
            onChange={handleSelectAllClick}
            indeterminate={selectedRows.length > 0 && selectedRows.length < rows.length}
          />
        </div>
      ),
      width: 60,
      renderCell: (params: any) => (
        <Checkbox
          color="primary"
          checked={selectedRows.includes(params.row.id)}
          onChange={handleCheckboxChange(params.row.id)}
        />
      ),
    },
    {
      field: "name",
      headerName: "Name",
      width: 450,
    },
    {
      field: "start_date",
      headerName: "Start Date",
      width: 150,
    },
    {
      field: "end_date",
      headerName: "End Date",
      width: 150,
    },
    {
      field: "active",
      headerName: "Active",
      width: 150,
      renderCell: (params: any) => (
        <Switch
          checked={params.row.active}
          onChange={handleCampaignFieldChange(params.row, EditableFields.Active)}
          disabled={isLoading || params.row.archived}
        />
      ),
    },
    {
      field: "archived",
      headerName: "Archived",
      width: 150,
      renderCell: (params: any) => (
        <Switch
          color="secondary"
          checked={params.row.archived}
          onChange={handleCampaignFieldChange(params.row, EditableFields.Archived)}
          disabled={isLoading || params.row.active}
        />
      ),
    },
    { field: "matchedAccounts", headerName: "Matched Accounts", width: 250 },
    { field: "clicks", headerName: "Clicks", width: 150, sortComparator: flatSortComparator },
    {
      field: "clickThroughRate",
      headerName: "CTR",
      width: 120,
      sortComparator: percentageSortComparator,
    },
    { field: "impressions", headerName: "Impressions", width: 250, sortComparator: flatSortComparator },
    { field: "budgetSpent", headerName: "Budget Spent", width: 250, sortComparator: currencySortComparator },
    { field: "costPerClick", headerName: "CPC (USD)", width: 150, sortComparator: currencySortComparator },
    { field: "costPerMile", headerName: "CPM (USD)", width: 150, sortComparator: currencySortComparator },
    { field: "conversions", headerName: "Conversions", width: 250, sortComparator: flatSortComparator },
  ];

  const setRowsAccordingly = useCallback(() => {
    let isMounted = true;

    const fetchRows = async () => {
      setIsLoading(true);
      if (selectedTimeFrame === ReportTimeFrames.CUSTOM_DATE && customReportDate && !isCustomDateChanged) {
        if (isMounted) setRows(campaignCustomReports);
        if (isMounted) setIsLoading(false);
        return;
      }

      const rowsToSet = await Promise.all(
        campaignPools.map(async (campaignPool) => {
          const {
            brandgenCampaignPoolId,
            name,
            start_date,
            end_date,
            active,
            archived,
            campaignPoolReports,
            accounts,
          } = campaignPool;

          let campaignReport = campaignPoolReports?.[0];

          if (selectedTimeFrame === ReportTimeFrames.CUSTOM_DATE && customReportDate && isCustomDateChanged) {
            try {
              campaignReport = await fetchCampaignDailyReport(brandgenCampaignPoolId, customReportDate);
            } catch (error) {
              // eslint-disable-next-line no-console
              console.error("Error fetching custom date campaign report: ", error);
            }
          }

          const { clicks, ctr, impressions, totalSpend, cpm, cpc, totalConversions } = campaignReport || {};

          return {
            id: brandgenCampaignPoolId,
            name,
            start_date: new Date(start_date).toLocaleDateString(),
            end_date: new Date(end_date).toLocaleDateString(),
            active,
            archived,
            matchedAccounts: accounts ?? 0,
            clicks: transformRowValue(clicks ?? 0, true, false, "FLAT"),
            clickThroughRate: transformRowValue(ctr ?? 0, false, true, "PERCENTAGE"),
            impressions: transformRowValue(impressions ?? 0, true, false, "FLAT"),
            budgetSpent: transformRowValue(totalSpend ?? 0, false, true, "CURRENCY"),
            costPerMile: transformRowValue(cpm ?? 0, false, true, "CURRENCY"),
            costPerClick: transformRowValue(cpc ?? 0, false, true, "CURRENCY"),
            conversions: transformRowValue(totalConversions ?? 0, true, false, "FLAT"),
          };
        }),
      );

      if (isMounted) {
        setRows(rowsToSet);
        setIsLoading(false);

        if (selectedTimeFrame === ReportTimeFrames.CUSTOM_DATE && customReportDate) {
          setCampaignCustomReports(rowsToSet);
        }
      }
    };

    fetchRows();

    return () => {
      isMounted = false;
    };
  }, [campaignPools, selectedTimeFrame, customReportDate, isCustomDateChanged, campaignCustomReports]);

  const convertToReadable = (str: string) => {
    return str
      .replace(/_/g, " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const transformObject = (obj: Record<string, any>): Record<string, any> => {
    const result: Record<string, any> = {};
    for (const [key, value] of Object.entries(obj)) {
      if (!excludedKeys.has(key)) {
        const readableKey = convertToReadable(key);
        result[readableKey] = value;
      }
    }
    return result;
  };

  const prepareDataForExport = async () => {
    try {
      setIsExportLoading(true);
      const exportData: any[] = [];
      const filteredCampaigns = campaignPools.filter((campaignPool) =>
        selectedRows.includes(campaignPool.brandgenCampaignPoolId),
      );

      if (filteredCampaigns.length === 0) {
        setIsExportLoading(false);
        infoToaster("Please, select campaigns to export");
        return;
      }

      /* eslint-disable no-await-in-loop */
      for (const campaignPool of filteredCampaigns) {
        const campaignData = {
          CampaignName: campaignPool.name,
          Active: campaignPool.active,
          Description: campaignPool.description,
          StartDate: campaignPool.start_date,
          EndDate: campaignPool.end_date,
          DailyBudget: `$${campaignPool.daily_budget}`,
          CampaignBudget: `$${campaignPool.campaign_budget}`,
          Accounts: campaignPool.accounts ? campaignPool.accounts : "0",
          Archived: campaignPool.archived,
        };

        let fetchedAdLibCampaignReports: AdLibCampaignWithReports[] = [];

        if (selectedTimeFrame === ReportTimeFrames.CUSTOM_DATE && customReportDate && isCustomDateChanged) {
          const response = await fetchLineItemsDailyReport(campaignPool.brandgenCampaignPoolId, customReportDate);

          // eslint-disable-next-line no-continue
          if (!response) continue;

          const { adLibCampaignReports: responseAdLibCampaignReports, adLibCampaigns } = response;

          setAdLibCampaignReports(responseAdLibCampaignReports);

          fetchedAdLibCampaignReports = adLibCampaigns.map((item: any, index: any) => ({
            ...item,
            adLibCampaignReports: [responseAdLibCampaignReports[index]],
          }));
        } else {
          fetchedAdLibCampaignReports = await fetchLineItemsWithReport(
            campaignPool.brandgenCampaignPoolId,
            selectedTimeFrame,
          );

          if (selectedTimeFrame === ReportTimeFrames.CUSTOM_DATE && customReportDate && !isCustomDateChanged) {
            fetchedAdLibCampaignReports = fetchedAdLibCampaignReports.map((item: any, index: any) => ({
              ...item,
              lineItemReports: [adLibCampaignReports[index]],
            }));
          }
        }

        const reportsHeaders = Object.keys(
          transformObject(fetchedAdLibCampaignReports?.[0]?.adLibCampaignReports?.[0] ?? {}),
        );

        if (fetchedAdLibCampaignReports && fetchedAdLibCampaignReports.length > 0) {
          const headers = ["Line Item Name", ...campaignHeaders, ...lineItemsHeaders, ...reportsHeaders];

          exportData.push(headers);

          fetchedAdLibCampaignReports.forEach((adLibCampaign: AdLibCampaignWithReports) => {
            let adLibCampaignReport: any = {};
            if (adLibCampaign.adLibCampaignReports && adLibCampaign.adLibCampaignReports.length > 0) {
              adLibCampaignReport = transformObject(adLibCampaign.adLibCampaignReports[0]);
            }

            exportData.push({
              LineItemName: adLibCampaign.campaignName ?? "N/A",
              ...campaignData,
              "Time Frame":
                selectedTimeFrame === ReportTimeFrames.CUSTOM_DATE && customReportDate
                  ? customReportDate
                  : selectedTimeFrame,
              LineItemBudget: `$${adLibCampaign.campaignBudget}`,
              // LineItemSpend: `$${adLibCampaign.adLibCampaignReports[0].totalSpend}`,
              LineItemActive: adLibCampaign.active,
              ...adLibCampaignReport,
            });
          });
        } else {
          exportData.push({ data: `No line items in ${campaignPool.name}` });
        }
        exportData.push({});
        exportData.push({});
      }

      csvDownload({ data: exportData, filename: "BrandGen.io-Campaigns.csv", delimiter: "," });
      setSelectedRows([]);
      setIsExportLoading(false);
    } catch (error) {
      const err = error as AxiosError;
      // eslint-disable-next-line no-console
      console.log("Error in prepareDataForExport: ", err.message);
      setIsExportLoading(false);
    }
  };

  useEffect(() => {
    setRowsAccordingly();
  }, [setRowsAccordingly, selectedTimeFrame]);

  useEffect(() => {
    if (!isOpen && editedRows.length !== 0) {
      setRowsAccordingly();
      setEditedRows([]);
    }
    if (!isOpen && selectedRows.length !== 0) {
      setSelectedRows([]);
    }
  }, [editedRows.length, isOpen, setRowsAccordingly, selectedRows]);

  return (
    <Dialog fullScreen open={isOpen} onClose={handleClose} TransitionComponent={Transition}>
      <AppBar className={muiClasses.appBar}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={handleClose} aria-label="close" disabled={isLoading}>
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" className={muiClasses.title}>
            "{advertiserName}" Advertiser Campaigns
          </Typography>
          <div className={muiClasses.timeFrameSelector}>
            <TimeFrameSelector
              selectedTimeFrame={selectedTimeFrame}
              handleTimeFrameChange={handleTimeFrameChange}
              disablePortal
            />
          </div>
          <Button
            className={muiClasses.button}
            variant="text"
            startIcon={<GetApp />}
            loaderSize={20}
            isLoading={isExportLoading}
            onClick={prepareDataForExport}
          >
            Export
          </Button>
          <Button
            className={muiClasses.button}
            variant="text"
            loaderColor="#FFF"
            onClick={handleSave}
            disabled={isLoading}
          >
            Save
          </Button>
        </Toolbar>
      </AppBar>

      <div className={muiClasses.container}>
        {isLoading && <Loader />}

        <BaseDataGrid columns={columns} rows={rows} />
      </div>
    </Dialog>
  );
};

export default CampaignsDialog;
